import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getStreamers,
  deleteStreamer,
  StreamerData,
} from '@/application/requests/streamerManagement';

export interface StreamerState {
  streamers: StreamerData[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  deleteLoading: boolean;
  deleteError: string | null;
}

const initialState: StreamerState = {
  streamers: [],
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  pageSize: 10,
  deleteLoading: false,
  deleteError: null,
};

export const fetchStreamers = createAsyncThunk(
  'streamer/fetchStreamers',
  async (params: { page?: number; limit?: number; keyword?: string }, { rejectWithValue }) => {
    try {
      const response = await getStreamers(params);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to fetch streamers');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch streamers');
    }
  }
);

export const removeStreamer = createAsyncThunk(
  'streamer/removeStreamer',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await deleteStreamer(id);
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to delete streamer');
      }
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete streamer');
    }
  }
);

const streamerSlice = createSlice({
  name: 'streamer',
  initialState,
  reducers: {
    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    clearError(state) {
      state.error = null;
      state.deleteError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStreamers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStreamers.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.streamers = action.payload.data;
          state.totalItems = action.payload.total;
          state.totalPages = action.payload.total_pages;
        }
      })
      .addCase(fetchStreamers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(removeStreamer.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(removeStreamer.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.streamers = state.streamers.filter((s) => s.id !== action.payload);
        state.totalItems = Math.max(0, state.totalItems - 1);
      })
      .addCase(removeStreamer.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload as string;
      });
  },
});

export const { setCurrentPage, setPageSize, clearError } = streamerSlice.actions;
export default streamerSlice.reducer;
